import { Link } from "react-router";
import i18n, { type TFunction } from "i18next";
import {
  type Column,
  type ColumnDef,
  type RowData,
  type SortingFn,
  type Table as TableInstance,
} from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import type { DocumentSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import { formatDate } from "~/helpers/date";
import { printFullName } from "~/helpers/user";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import MoreTooltip from "~/molecules/MoreTooltip";
import { TagsValue } from "~/molecules/Tags";
import type { EditableField } from "~/helpers/libraryTypes";
import { getNoTierLabel, getTierTitle } from "~/helpers/tier";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required for interface merging
  interface ColumnMeta<TData extends RowData, TValue> {
    label?: string;
  }
}

type DocumentsColumnId =
  | "title"
  | "tier"
  | "category"
  | "referenceNumber"
  | "tag"
  | "owner"
  | "publisher"
  | "links"
  | "publishedAt"
  | "adoptedAt"
  | "reviewFrom"
  | "reviewTo";

type DocumentsColumnGroupId =
  "metadata" | "latestPublishedVersion" | "nextReview";
type DocumentsFeatureFlag = "referenceNumber";

interface DocumentsColumnSpec {
  id: DocumentsColumnId;
  getLabel: (t: TFunction) => string;
  group?: DocumentsColumnGroupId;
  editableField?: EditableField;
  featureFlag?: DocumentsFeatureFlag;
}

interface CreateDocumentsColumnsParams {
  t: TFunction;
  currentUserId: number;
  filteredTagIds: string[];
  columnSpecs: DocumentsColumnSpec[];
}

interface ResolveDocumentsColumnMenuParams {
  columnSpecs: DocumentsColumnSpec[];
  table: TableInstance<DocumentSummary>;
  t: TFunction;
}

interface ColumnBuilderContext {
  currentUserId: number;
  spec: DocumentsColumnSpec;
  t: TFunction;
  filteredTagIds: Set<string>;
}

export interface ResolvedDocumentsColumnGroup {
  id: string;
  label: string;
  columns: Column<DocumentSummary, unknown>[];
}

const columnGroupLabelById: Record<
  DocumentsColumnGroupId,
  (t: TFunction) => string
> = {
  metadata: (t) => t(($) => $.commons.document.metadata),
  latestPublishedVersion: (t) =>
    t(($) => $.commons.document.latestPublishedVersion),
  nextReview: (t) => t(($) => $.commons.workflow.nextReviewPeriod),
};

const documentsColumnSpecs: DocumentsColumnSpec[] = [
  {
    id: "referenceNumber",
    getLabel: (t) => t(($) => $.commons.document.referenceNumber),
    group: "metadata",
    editableField: "referenceNumber",
    featureFlag: "referenceNumber",
  },
  {
    id: "title",
    getLabel: (t) => t(($) => $.commons.document.title),
  },
  {
    id: "tier",
    getLabel: (t) => t(($) => $.commons.document.tier),
    group: "metadata",
    editableField: "tierId",
  },
  {
    id: "category",
    getLabel: (t) => t(($) => $.commons.document.category),
    group: "metadata",
    editableField: "categoryId",
  },
  {
    id: "tag",
    getLabel: (t) => t(($) => $.commons.document.tags),
    group: "metadata",
    editableField: "tags",
  },
  {
    id: "owner",
    getLabel: (t) => t(($) => $.commons.document.owner),
    group: "metadata",
    editableField: "ownerId",
  },
  {
    id: "publisher",
    getLabel: (t) => t(($) => $.commons.document.publisher),
    group: "metadata",
    editableField: "publisherId",
  },
  {
    id: "links",
    getLabel: (t) => t(($) => $.commons.document.links),
    group: "metadata",
    editableField: "links",
  },
  {
    id: "publishedAt",
    getLabel: (t) => t(($) => $.commons.document.publishedAt),
    group: "latestPublishedVersion",
  },
  {
    id: "adoptedAt",
    getLabel: (t) => t(($) => $.commons.document.adoptedAt),
    group: "latestPublishedVersion",
  },
  {
    id: "reviewFrom",
    getLabel: (t) => t(($) => $.commons.document.reviewFrom),
    group: "nextReview",
    editableField: "reviewFrom",
  },
  {
    id: "reviewTo",
    getLabel: (t) => t(($) => $.commons.document.reviewTo),
    group: "nextReview",
    editableField: "reviewTo",
  },
] as const;

const editableFieldLabelByField = Object.fromEntries(
  documentsColumnSpecs.flatMap((spec) =>
    spec.editableField ? [[spec.editableField, spec.getLabel]] : [],
  ),
) as Record<EditableField, (t: TFunction) => string>;

const naturalSort: SortingFn<DocumentSummary> = (rowA, rowB, columnId) =>
  new Intl.Collator(i18n.language, { numeric: true }).compare(
    String(rowA.getValue(columnId) ?? ""),
    String(rowB.getValue(columnId) ?? ""),
  );

const SortableColumnHeader = ({
  column,
  label,
}: {
  column: Column<DocumentSummary, unknown>;
  label: string;
}) => (
  <Button
    variant="ghost"
    className="h-auto p-0 hover:bg-transparent hover:underline"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  >
    {label}
    {column.getIsSorted() === "asc" ? (
      "↑"
    ) : column.getIsSorted() === "desc" ? (
      "↓"
    ) : (
      <ArrowUpDown className="size-4 opacity-50" />
    )}
  </Button>
);

const sortableHeader: ColumnDef<DocumentSummary>["header"] = ({ column }) => (
  <SortableColumnHeader
    column={column}
    label={column.columnDef.meta?.label ?? ""}
  />
);

const columnBuilders: Record<
  DocumentsColumnId,
  (context: ColumnBuilderContext) => ColumnDef<DocumentSummary>
> = {
  title: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.name,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => (
      <Link
        to={`/documents/${row.original.id}?versionId=${row.original.latestPublishedVersion?.id ?? row.original.latestVersion.id}&tab=metadata`}
        className="hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  }),
  tier: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => getTierTitle(row.tier?.title, t),
    cell: ({ row }) =>
      row.original.tier ? (
        row.original.tier.title
      ) : (
        <span className="text-muted-foreground">{getNoTierLabel(t)}</span>
      ),
  }),
  category: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.category.title,
    cell: ({ row }) => row.original.category.title,
  }),
  referenceNumber: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.referenceNumber ?? "",
    cell: ({ row }) => row.original.referenceNumber ?? "-",
  }),
  tag: ({ spec, t, filteredTagIds }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => {
      const titles = row.tags.map((tag) => tag.title).sort();
      return titles.length > 0 ? titles.join(", ") : undefined;
    },
    sortUndefinedLast: true,
    cell: ({ row }) => {
      const docTags = [...row.original.tags].sort((a, b) => {
        const aMatch = filteredTagIds.has(a.id.toString());
        const bMatch = filteredTagIds.has(b.id.toString());
        if (aMatch !== bMatch) return aMatch ? -1 : 1;
        return a.title.localeCompare(b.title);
      });

      return (
        <div className="flex items-center gap-1">
          {docTags.length > 0 ? (
            <TagsValue>{docTags[0]!.title}</TagsValue>
          ) : (
            <span>-</span>
          )}
          <MoreTooltip data={docTags.map((tag) => tag.title)} variant="tag" />
        </div>
      );
    },
  }),
  owner: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => printFullName(row.owner),
    cell: ({ row }) => (
      <AvatarWithFallback user={row.original.owner} size="xs" showTooltip />
    ),
  }),
  publisher: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => printFullName(row.publisher),
    cell: ({ row }) =>
      row.original.publisher ? (
        <AvatarWithFallback
          user={row.original.publisher}
          size="xs"
          showTooltip
        />
      ) : (
        "-"
      ),
  }),
  links: ({ spec, t }) => ({
    id: spec.id,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.linkedDocumentIds.length,
    cell: ({ row }) => {
      const totalLinks = row.original.linkedDocumentIds.length;
      return totalLinks > 0 ? totalLinks : "-";
    },
  }),
  publishedAt: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestPublishedVersion?.publishedAt ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) =>
      formatDate(row.original.latestPublishedVersion?.publishedAt ?? null),
  }),
  adoptedAt: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestPublishedVersion?.adoptedAt ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) =>
      formatDate(row.original.latestPublishedVersion?.adoptedAt ?? null),
  }),
  reviewFrom: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestVersion.reviewFrom ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.latestVersion.reviewFrom),
  }),
  reviewTo: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestVersion.reviewTo ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.latestVersion.reviewTo),
  }),
};

export const getDocumentsListColumnSpecs = (
  enabledFeatureFlags: Record<DocumentsFeatureFlag, boolean>,
): DocumentsColumnSpec[] =>
  documentsColumnSpecs.filter(
    (spec) => !spec.featureFlag || enabledFeatureFlags[spec.featureFlag],
  );

export const createDocumentsColumns = ({
  t,
  currentUserId,
  filteredTagIds,
  columnSpecs,
}: CreateDocumentsColumnsParams): ColumnDef<DocumentSummary>[] => {
  const filteredTagIdSet = new Set(filteredTagIds);
  return columnSpecs.map((spec) =>
    columnBuilders[spec.id]({
      currentUserId,
      spec,
      t,
      filteredTagIds: filteredTagIdSet,
    }),
  );
};

export const getEditableFieldByColumnId = (
  columnSpecs: DocumentsColumnSpec[],
): Partial<Record<string, EditableField>> =>
  Object.fromEntries(
    columnSpecs.flatMap((spec) =>
      spec.editableField ? [[spec.id, spec.editableField]] : [],
    ),
  );

export const getEditableFieldLabel = (
  field: EditableField,
  t: TFunction,
): string => {
  const getLabel = editableFieldLabelByField[field];
  if (!getLabel) {
    throw new Error(`Missing label for editable field: ${field}`);
  }

  return getLabel(t);
};

export const resolveDocumentsColumnMenu = ({
  columnSpecs,
  table,
  t,
}: ResolveDocumentsColumnMenuParams): {
  columnMenuLabels: Record<string, string>;
  resolvedGroups: ResolvedDocumentsColumnGroup[];
  ungroupedColumns: Column<DocumentSummary, unknown>[];
} => {
  const columnMenuLabels = Object.fromEntries(
    columnSpecs.map((spec) => [spec.id, spec.getLabel(t)]),
  );
  const hideableColumns = table
    .getAllColumns()
    .filter((column) => column.getCanHide());
  const groupedSpecs = (
    Object.keys(columnGroupLabelById) as DocumentsColumnGroupId[]
  ).map((groupId) => {
    const groupColumnIds = columnSpecs
      .filter((spec) => spec.group === groupId)
      .map((spec) => spec.id);

    return {
      id: groupId,
      label: columnGroupLabelById[groupId](t),
      columnIds: groupColumnIds,
    };
  });
  const groupedColumnIds = new Set<string>(
    groupedSpecs.flatMap((group) => group.columnIds),
  );
  const ungroupedColumns = hideableColumns.filter(
    (column) => !groupedColumnIds.has(column.id),
  );
  const resolvedGroups = groupedSpecs
    .map((group) => ({
      ...group,
      columns: group.columnIds
        .map((columnId) => table.getColumn(columnId))
        .filter((column): column is NonNullable<typeof column> => !!column),
    }))
    .filter((group) => group.columns.length > 0);

  return {
    columnMenuLabels,
    resolvedGroups,
    ungroupedColumns,
  };
};
